import { setKibanaLogFlush } from "@agentx/agentx-services";
import { createLogger as createBaseLogger } from "@uuip/unified-ui-platform-sdk";
import { DateTime } from "luxon";
// Mask the values of keys mentioned in this object
const objKeysToBeMasked = {
    ani: true,
    dn: true,
    defaultDn: true,
    deviceId: true,
    deviceUrl: true,
    dialNumber: true,
    dialNumberValueForNonUS: true,
    dnNumber: true,
    agent_ani: true,
    dnis: true,
    email: true,
    callAssociatedData: true,
    clientDeviceUri: true,
    extensionNumber: true,
    internationalPhoneNumber: true,
    phoneNumber: true,
    preferredDeviceId: true,
    title: true,
    text: true,
    displayName: true,
    sentBy: true,
    agentName: true,
    agentMailId: true,
    userName: true,
    emails: true,
    sipAddresses: true
};
const logger = createBaseLogger("AgentX", objKeysToBeMasked);
const KEY = "YTdiYTEwZjctYzIxYy00YzhhLTgzN2ItNDQ4MDNhOGMxZmEy";
export const LOGGER_HOST_KEY = "logger-host";
export var Level;
(function (Level) {
    Level[Level["Trace"] = 1] = "Trace";
    Level[Level["Debug"] = 2] = "Debug";
    Level[Level["Warn"] = 3] = "Warn";
    //eslint-disable-next-line @typescript-eslint/no-shadow
    Level[Level["Error"] = 4] = "Error";
    Level[Level["Fatal"] = 5] = "Fatal";
})(Level || (Level = {}));
const EVENT_KEY = "event=";
const ERROR_KEY = "error";
const FAILED_KEY = "failed";
const generateUUID = () => {
    let d = DateTime.utc().toMillis();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = ((d + Math.random() * 16) % 16) | 0; // eslint-disable-line no-bitwise
        d = Math.floor(d / 16);
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16); // eslint-disable-line no-bitwise
    });
};
const loggerId = generateUUID();
logger.info(`[Agentx] ClientLogSessionID =  ${loggerId}`);
localStorage.setItem("ClientLogSessionID", loggerId);
async function defaultSendKibanaLogs(host, logs, logPrefix, updateLastLogTS) {
    try {
        const res = await fetch(new URL("api/pushLogs", host), {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: {
                Authorization: KEY,
                "Content-Type": "application/json",
                Accept: "application/json, text/plain, */*"
            },
            body: JSON.stringify(logs)
        });
        if (res.ok) {
            updateLastLogTS();
            logger.info(`${logPrefix} pushlogs api success`);
        }
        else {
            logger.warn(`${logPrefix} pushlogs api unsuccessful`);
        }
    }
    catch (e) {
        logger.error(`${logPrefix} pushlogs api failed`, e);
    }
}
function isStructuredLog(value) {
    if (value.msgs.length > 0) {
        const msg0 = value.msgs[0];
        const key = "logVer";
        if (typeof msg0 === "object") {
            return key in msg0;
        }
    }
    return false;
}
function getLogVersion(value) {
    if (isStructuredLog(value)) {
        const msg0 = value.msgs[0];
        const key = "logVer";
        if (typeof msg0 === "object") {
            if (key in msg0) {
                return msg0[key];
            }
            else {
                return 0;
            }
        }
    }
    return 0;
}
function toLogsArray(logs) {
    const isLogRecord = (v) => {
        if (!v || typeof v !== "object") {
            return false;
        }
        const o = v;
        return (typeof o.pfx === "string" &&
            Array.isArray(o.msgs) &&
            o.msgs.every((m) => typeof m === "string" || typeof m === "object") &&
            typeof o.ts === "number" &&
            typeof o.lvl === "number");
    };
    if (!logs || logs === "") {
        return [];
    }
    const segments = logs.split("<uuip-eol>").filter((s) => s && s.trim() !== "");
    if (segments.length === 0) {
        return [];
    }
    try {
        const raw = JSON.parse(`[${segments.join(",")}]`);
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw.filter((v) => isLogRecord(v));
    }
    catch (e) {
        logger.warn("Failed to parse uuip logs", e);
        return [];
    }
}
/*eslint @typescript-eslint/no-unused-vars: "off"*/
const buildLogs = (orgId, agentId, logsArr, lastLogTs) => {
    const lastTs = lastLogTs && lastLogTs !== "" ? Number.parseInt(lastLogTs) : 0;
    const filteredLogs = logsArr.filter((value) => {
        if (isStructuredLog(value)) {
            const logVer = getLogVersion(value);
            return value.ts > lastTs && logVer >= 1 && value.lvl >= Level.Debug ? true : false;
        }
        // Don't send trace logs to Kibana
        if (value.lvl <= Level.Trace) {
            return false;
        }
        const searchMsg = value.msgs.join(" | ").toLowerCase();
        return (value.ts > lastTs &&
            (searchMsg.includes(EVENT_KEY) ||
                searchMsg.includes(FAILED_KEY) ||
                searchMsg.includes(ERROR_KEY) ||
                value.lvl > Level.Debug));
    });
    if (filteredLogs.length > 0) {
        const selectedLogs = [];
        for (let l = 0; l < filteredLogs.length; l++) {
            const record = filteredLogs[l];
            let logLevel;
            switch (record.lvl) {
                case Level.Trace:
                    logLevel = "trace";
                    break;
                case Level.Debug:
                    logLevel = "info";
                    break;
                case Level.Warn:
                    logLevel = "warn";
                    break;
                case Level.Error:
                    logLevel = "error";
                    break;
                case Level.Fatal:
                    logLevel = "error";
                    break;
            }
            const getLogMessage = () => {
                if (!isStructuredLog(record) || getLogVersion(record) <= 0) {
                    return record.msgs
                        .map((message) => (typeof message === "string" ? message : JSON.stringify(message)))
                        .join(" | ");
                }
                return JSON.stringify(record.msgs);
            };
            selectedLogs.push({
                level: logLevel,
                message: `ClientLogSessionID=${loggerId} | TimeStamp=${new Date(record.ts)} org_id=${orgId} | agentId=${agentId} | ${getLogMessage()}`,
                timestamp: new Date(record.ts),
                clientLogSessionId: loggerId,
                agentId,
                orgId
            });
        }
        return selectedLogs;
    }
    return [];
};
export async function logMiner(sentKibanaLogsFunc = defaultSendKibanaLogs) {
    const host = sessionStorage.getItem(LOGGER_HOST_KEY);
    if (!host) {
        return Promise.reject(new Error(`${LOGGER_HOST_KEY} not initialised in sessionStorage!`));
    }
    const orgId = sessionStorage.getItem("orgId") || "";
    const agentId = sessionStorage.getItem("agentId") || "";
    const uuipLogs = sessionStorage.getItem("uuip-client-logs") || "";
    const lastLogTs = localStorage.getItem("last-log-ts") || "";
    const logsArr = toLogsArray(uuipLogs);
    if (logsArr.length === 0) {
        return Promise.resolve();
    }
    const kibanaLogs = buildLogs(orgId, agentId, logsArr, lastLogTs);
    if (kibanaLogs.length === 0) {
        return Promise.resolve();
    }
    const updateLastLogTS = () => {
        if (logsArr.length > 0) {
            const lastTimestamp = logsArr[logsArr.length - 1].ts;
            localStorage.setItem("last-log-ts", lastTimestamp.toString());
        }
    };
    return sentKibanaLogsFunc(host, kibanaLogs, "[LogMiner]", updateLastLogTS);
}
setInterval(() => {
    logMiner().catch((e) => {
        logger.error("logMiner failed", e);
    });
}, 300000);
setKibanaLogFlush(() => logMiner());
export const createLogger = (prefix) => {
    class LoggerWrapper {
        constructor(pfx) {
            this.prefix = pfx;
        }
        error(...msg) {
            logger.error(this.prefix, ...msg);
        }
        info(...msg) {
            logger.info(this.prefix, ...msg);
        }
        warn(...msg) {
            logger.warn(this.prefix, ...msg);
        }
        debug(...msg) {
            logger.debug(this.prefix, ...msg);
        }
    }
    return new LoggerWrapper(prefix);
};
/** this will catch the unhandled javascript errors and update desktop standard loggers */
window.addEventListener("error", (event) => {
    logger.error("Unhandled exception", event.message, event.filename, event.lineno, event.colno, event.error);
});
